import React, { useRef } from 'react';
import { User } from '../../types';
import { isValidImageFile } from '../../lib/security';

interface AvatarProps {
  user: Pick<User, 'firstName' | 'lastName' | 'avatar'>;
  size?: number;
  editable?: boolean;
  onUpload?: (dataUrl: string) => void;
  onError?: (key: string) => void;
}

export const Avatar: React.FC<AvatarProps> = ({
  user, size = 48, editable = false, onUpload, onError,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const initials = `${user.firstName[0] ?? ''}${user.lastName[0] ?? ''}`.toUpperCase();

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const { ok, error } = isValidImageFile(file);
    if (!ok) { onError?.(error!); return; }
    const reader = new FileReader();
    reader.onload = (ev) => { if (ev.target?.result) onUpload?.(ev.target.result as string); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <div
      className={`avatar ${editable ? 'avatar-editable' : ''}`}
      style={{ width: size, height: size, fontSize: size * 0.35 }}
      onClick={() => editable && fileRef.current?.click()}
      role={editable ? 'button' : undefined}
      aria-label={editable ? 'Change profile picture' : undefined}
      tabIndex={editable ? 0 : undefined}
      onKeyDown={(e) => { if (editable && (e.key === 'Enter' || e.key === ' ')) fileRef.current?.click(); }}
    >
      {user.avatar
        ? <img src={user.avatar} alt={`${user.firstName} ${user.lastName}`} className="avatar-img" />
        : <span className="avatar-initials">{initials}</span>
      }
      {editable && (
        <div className="avatar-overlay">📷</div>
      )}
      {editable && (
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
          style={{ display: 'none' }}
          onChange={handleFile}
          aria-hidden="true"
        />
      )}
    </div>
  );
};
